import { supabase } from "@/lib/supabase";
import { getAccount } from "@/lib/profile";
import type { Account, Subscription } from "@/lib/types";
import { GRACE_DAYS, TRIAL_DAYS } from "@/lib/config";

/**
 * Billing / access layer. A user "has access" if either:
 *   - their account has an active or trialing Stripe subscription, or
 *   - the account is still within the in-app free trial (TRIAL_DAYS from
 *     the account's own creation date — not the caller's personal
 *     profiles.created_at, so every member shares one trial clock; a
 *     Member invited into an account whose trial already lapsed doesn't
 *     get a fresh one of their own, backlog AUTH-6), or
 *   - a subscription that WAS active/trialing lapsed less than GRACE_DAYS
 *     ago (`tier: "grace"`, backlog LAUNCH-9) — read-only access, not full.
 * The subscriptions row is written only by the Stripe webhook; the frontend
 * reads it (RLS-scoped via is_account_member()) but never writes it.
 */

export type AccessReason = "subscription" | "trial" | "none";
/**
 * "full" = normal access. "grace" = a lapsed subscription within its
 * read-only window — view/export only, no new work (see App.tsx's
 * requireAccess). "locked" = past grace (or a trial that expired with no
 * subscription ever) — the existing full-screen Paywall wall, unchanged.
 */
export type AccessTier = "full" | "grace" | "locked";

/** The pure decision itself — computeAccessState()'s own return shape, unchanged (and still exhaustively unit-tested, backlog TEST-1) by the account passthrough AccessState adds below. */
export interface AccessDecision {
  hasAccess: boolean;
  reason: AccessReason;
  tier: AccessTier;
  /** Whole days left in the free trial (only meaningful when reason === "trial"). */
  trialDaysLeft: number | null;
  /** Whole days left in the read-only grace period (only meaningful when tier === "grace"). */
  graceDaysLeft: number | null;
  subscription: Subscription | null;
}

export interface AccessState extends AccessDecision {
  /**
   * The account row getAccessState() already fetched to make the decision
   * above — exposed so a caller that also needs an account field (e.g.
   * App.tsx's business_name onboarding gate) can read it straight off this
   * result instead of firing its own redundant getAccount() call on every
   * authenticated page load (backlog PERF-8, the same shape PERF-2 already
   * fixed elsewhere). Null on a fetch failure (see getAccessState's own
   * catch fallback in App.tsx) as well as for a caller with genuinely no
   * account row — in practice only the former, since every real signed-up
   * user gets one unconditionally (handle_new_user()).
   */
  account: Account | null;
}

export async function getSubscription(): Promise<Subscription | null> {
  const { data, error } = await supabase.from("subscriptions").select("*").maybeSingle();
  if (error) throw error;
  return (data as Subscription | null) ?? null;
}

/**
 * Pure decision logic (backlog TEST-1 — the fourth audit's highest-risk
 * finding: a boundary bug here directly changes who can use the paid app).
 * Extracted out of getAccessState() so it's unit-testable without mocking
 * Supabase — takes `now` explicitly (defaults to the real clock) so
 * day-boundary math is deterministic in tests.
 */
export function computeAccessState(
  subscription: Subscription | null,
  account: Pick<Account, "created_at" | "trial_reset_at"> | null,
  now: number = Date.now(),
): AccessDecision {
  if (subscription && (subscription.status === "active" || subscription.status === "trialing")) {
    return {
      hasAccess: true,
      reason: "subscription",
      tier: "full",
      trialDaysLeft: null,
      graceDaysLeft: null,
      subscription,
    };
  }

  // A subscription that WAS active/trialing and has since lapsed gets a
  // read-only grace window, anchored to when the webhook set
  // access_locked_at — not applied to a first-time trial expiring with no
  // prior subscription (see this file's own header comment).
  if (subscription?.access_locked_at) {
    const elapsedDays = (now - new Date(subscription.access_locked_at).getTime()) / 86_400_000;
    const daysLeft = Math.ceil(GRACE_DAYS - elapsedDays);
    if (daysLeft > 0) {
      return {
        hasAccess: true,
        reason: "subscription",
        tier: "grace",
        trialDaysLeft: null,
        graceDaysLeft: daysLeft,
        subscription,
      };
    }
  }

  // Fall back to the in-app free trial, anchored to the ACCOUNT's own
  // creation date (shared by every member) rather than the caller's own
  // profiles.created_at — or to trial_reset_at, when an admin has granted
  // a fresh trial (backlog ADMIN-11), which overrides created_at entirely.
  const createdAt = account?.trial_reset_at ?? account?.created_at;
  if (createdAt) {
    const elapsedDays = (now - new Date(createdAt).getTime()) / 86_400_000;
    const daysLeft = Math.ceil(TRIAL_DAYS - elapsedDays);
    if (daysLeft > 0) {
      return {
        hasAccess: true,
        reason: "trial",
        tier: "full",
        trialDaysLeft: daysLeft,
        graceDaysLeft: null,
        subscription,
      };
    }
  }

  return {
    hasAccess: false,
    reason: "none",
    tier: "locked",
    trialDaysLeft: 0,
    graceDaysLeft: null,
    subscription,
  };
}

export async function getAccessState(): Promise<AccessState> {
  // Fetched together (rather than the account only when needed) so the
  // decision itself can live in one pure, fully-testable function above —
  // a second small RLS-scoped point query on every access check, traded
  // deliberately for real test coverage of the single highest-risk
  // decision in the app (backlog TEST-1). The full account row is passed
  // straight through onto the result too (backlog PERF-8), not just the
  // Pick computeAccessState itself needs — see AccessState's own comment.
  const [subscription, account] = await Promise.all([getSubscription(), getAccount()]);
  return { ...computeAccessState(subscription, account), account };
}

/** Start Stripe Checkout for a subscription and redirect the browser to it. */
export async function startCheckout(): Promise<void> {
  const { data, error } = await supabase.functions.invoke("stripe-checkout", {
    body: { returnUrl: window.location.origin },
  });
  if (error) throw error;
  const url = (data as { url?: string })?.url;
  if (!url) throw new Error("Checkout could not be started. Please try again.");
  window.location.href = url;
}

/** Open the Stripe Billing Portal so the user can manage/cancel their plan. */
export async function openBillingPortal(): Promise<void> {
  const { data, error } = await supabase.functions.invoke("stripe-portal", {
    body: { returnUrl: `${window.location.origin}/account` },
  });
  if (error) throw error;
  const url = (data as { url?: string })?.url;
  if (url) window.location.href = url;
}
